import React, { useCallback, useRef, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { CloudUpload, AccountBalance } from '@mui/icons-material';

import {
  IFinTSImportResult,
  useFinTSInstituteCount,
  useImportFinTSInstitutes,
} from '../../hooks/reactQuerys/FinTSInstituteQueries';
import { useAlert } from '../../context/AlertContext';

type ImportMode = 'local' | 'migration' | 'both';

const MODE_LABEL: Record<ImportMode, string> = {
  local: 'Nur diese Instanz aktualisieren',
  migration: 'Nur Migration erstellen (alle Kunden beim nächsten Deploy)',
  both: 'Beides — lokal importieren UND Migration erzeugen',
};

const MODE_HELP: Record<ImportMode, string> = {
  local:
    'Schreibt die hochgeladene ZKA-Liste direkt in die FinTSInstitute-Tabelle dieser DiNo-Instanz. Keine Auswirkung auf andere Installationen.',
  migration:
    'Erzeugt eine datierte SQL-Migration unter backend-private/migration/. Diese landet beim nächsten Deployment in jeder Kunden-Instanz und überschreibt dort den Katalog.',
  both: 'Empfohlene Variante für den Hersteller-Admin: hier sofort sichtbar, alle Kunden bekommen das gleiche Update beim nächsten Update-Push.',
};

export const FinTSInstituteUpload: React.FC = () => {
  const { showAlert } = useAlert();
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [mode, setMode] = useState<ImportMode>('local');
  const [lastResult, setLastResult] = useState<IFinTSImportResult | null>(null);
  const [fileName, setFileName] = useState<string>('');

  const { instituteCount, lastUpdated, refetchCount } =
    useFinTSInstituteCount();
  const { importFinTSInstitutes, isImportingFinTSInstitutes } =
    useImportFinTSInstitutes();

  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result);
        const idx = result.indexOf(',');
        resolve(idx >= 0 ? result.slice(idx + 1) : result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleFile = useCallback(
    async (file: File) => {
      setFileName(file.name);
      setLastResult(null);
      try {
        const dataB64 = await fileToBase64(file);
        const result = await importFinTSInstitutes({
          FileName: file.name,
          DataBase64: dataB64,
          Mode: mode,
        });
        setLastResult(result);
        showAlert({
          severity: 'success',
          text: `Import abgeschlossen: ${result.parsed} Banken verarbeitet (${
            result.inserted ?? 0
          } neu, ${result.updated ?? 0} aktualisiert).`,
        });
        void refetchCount();
      } catch (e) {
        showAlert({
          severity: 'error',
          text:
            e instanceof Error
              ? `Import fehlgeschlagen: ${e.message}`
              : 'Import fehlgeschlagen.',
        });
      }
    },
    [mode, importFinTSInstitutes, refetchCount, showAlert]
  );

  return (
    <Box sx={{ mt: 6 }}>
      <Typography variant='h6' gutterBottom>
        <AccountBalance sx={{ mr: 1, verticalAlign: 'middle' }} />
        FinTS-Institute-Katalog (ZKA-Liste)
      </Typography>

      <Alert severity='info' sx={{ mb: 2 }}>
        <AlertTitle>Wozu?</AlertTitle>
        Beim Anlegen eines HBCI-Kontos wird die FinTS-URL automatisch anhand der
        BLZ vorbefüllt. Der Katalog stammt von der{' '}
        <a
          href='https://www.hbci-zka.de/institute/institut_auswahl.htm'
          target='_blank'
          rel='noopener noreferrer'
        >
          ZKA-Institut-Liste
        </a>{' '}
        und wird gelegentlich aktualisiert (Bank-Fusionen, neue URLs).
      </Alert>

      <Stack
        direction='row'
        spacing={2}
        alignItems='center'
        sx={{ mb: 2, color: 'text.secondary' }}
      >
        <Typography variant='body2'>
          <b>{instituteCount}</b> Banken aktuell im Katalog
        </Typography>
        {lastUpdated && (
          <Typography variant='body2'>
            · letztes Update {new Date(lastUpdated).toLocaleString('de-DE')}
          </Typography>
        )}
      </Stack>

      <FormControl sx={{ mb: 2 }}>
        <Typography variant='subtitle2' sx={{ mb: 1 }}>
          Import-Modus
        </Typography>
        <RadioGroup
          value={mode}
          onChange={e => setMode(e.target.value as ImportMode)}
        >
          {(['local', 'migration', 'both'] as ImportMode[]).map(m => (
            <FormControlLabel
              key={m}
              value={m}
              control={<Radio size='small' />}
              label={
                <Box>
                  <Typography variant='body2'>{MODE_LABEL[m]}</Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {MODE_HELP[m]}
                  </Typography>
                </Box>
              }
            />
          ))}
        </RadioGroup>
      </FormControl>

      <input
        ref={fileInput}
        type='file'
        accept='.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        hidden
        onChange={e => {
          const f = e.target.files?.[0];
          if (f) void handleFile(f);
          e.target.value = '';
        }}
      />

      <Button
        variant='contained'
        startIcon={
          isImportingFinTSInstitutes ? (
            <CircularProgress size={18} color='inherit' />
          ) : (
            <CloudUpload />
          )
        }
        onClick={() => fileInput.current?.click()}
        disabled={isImportingFinTSInstitutes}
      >
        {isImportingFinTSInstitutes
          ? `Verarbeite ${fileName} …`
          : 'fints.xlsx hochladen'}
      </Button>

      {lastResult && (
        <Alert severity='success' sx={{ mt: 2 }}>
          <AlertTitle>Import abgeschlossen</AlertTitle>
          {lastResult.parsed} Banken in der Datei.
          {lastResult.inserted !== undefined && (
            <>
              {' '}
              Davon {lastResult.inserted} neu, {lastResult.updated}{' '}
              aktualisiert.
            </>
          )}
          {lastResult.migrationFile && (
            <>
              <br />
              Migration geschrieben: <code>{lastResult.migrationFile}</code>
            </>
          )}
        </Alert>
      )}
    </Box>
  );
};
